/**
 * A Client's outreach settings (Story 35, task 138; ADR-0014, ADR-0021):
 * whether BlueSphere may ask the affected user, and their manager, whether a
 * suspicious alert was them. Off by default. Enabling needs ADR-0014's four
 * obligations confirmed — the API refuses otherwise — and the form says who
 * the users will see as the sender. Task 139 adds the channels that are not
 * ready yet (each shown with why) and the security contact, asked at half the
 * window when nobody has answered.
 *
 * Three tabs, so the dialog fits a laptop screen: Enable (the checklist and
 * the switch), Channels (with Slack's Connect on its own row, task 139c) and
 * Contact & limits. The title and the Save bar stay put; only the middle
 * scrolls. Every tab's values are kept while switching and saved together.
 */

import { useEffect, useState } from 'react';

import type {
  ClientTenant,
  OutreachChannel,
  OutreachChecklistItem,
  OutreachSecurityContact,
  OutreachSettingsUpdate,
} from '@bluesphere/api-client';
import { PillButton } from '@bluesphere/ui-kit';

import { ErrorNote, INPUT, ModalShell } from '@bluesphere/mdr-alert-panel';
import { Switch } from './ResponseSettingsModal';

const CHECKLIST: { key: OutreachChecklistItem; label: string; hint: string }[] = [
  {
    key: 'client_agreement',
    label: 'The Client has agreed in writing',
    hint: 'Their agreement covers BlueSphere contacting their staff about alerts.',
  },
  {
    key: 'communication_plan',
    label: 'The communication plan is delivered',
    hint: 'Staff have been told these messages may come, and how to tell them from phishing.',
  },
  {
    key: 'training_reconciled',
    label: 'Awareness training is reconciled',
    hint: 'Their training no longer tells staff to ignore every message that asks them to reply.',
  },
  {
    key: 'sender_identity',
    label: 'The Client knows the sender is "BlueSphere MDR"',
    hint: 'Messages come from BlueSphere MDR, not from your own brand.',
  },
];

const CHANNELS: { key: OutreachChannel; label: string }[] = [
  { key: 'email', label: 'Email' },
  { key: 'sms', label: 'Text message' },
  { key: 'voice', label: 'Phone call' },
  { key: 'teams', label: 'Microsoft Teams' },
  { key: 'slack', label: 'Slack' },
  { key: 'whatsapp', label: 'WhatsApp' },
];

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const E164 = /^\+[1-9][0-9]{7,14}$/;

/** The contact as the API takes it: null when every field is blank. */
function contactOf(name: string, email: string, phone: string): OutreachSecurityContact | null {
  if (!name.trim() && !email.trim() && !phone.trim()) return null;
  return { name: name.trim(), email: email.trim() || null, phone: phone.trim() || null };
}

function contactProblem(c: OutreachSecurityContact | null): string | null {
  if (c === null) return null;
  if (!c.name) return 'The security contact needs a name.';
  if (!c.email && !c.phone) return 'The security contact needs an email or a phone.';
  if (c.email && !EMAIL.test(c.email)) return "The security contact's email is not an address.";
  if (c.phone && !E164.test(c.phone)) return 'Write the phone in international form, like +15555550100.';
  return null;
}

type Tab = 'enable' | 'channels' | 'contact';

const TABS: { key: Tab; label: string }[] = [
  { key: 'enable', label: 'Enable' },
  { key: 'channels', label: 'Channels' },
  { key: 'contact', label: 'Contact & limits' },
];

export function OutreachSettingsModal({
  tenant,
  onCancel,
  onSubmit,
  onConnectSlack,
  onRefresh,
  isPending,
  error,
}: {
  tenant: ClientTenant;
  onCancel: () => void;
  onSubmit: (body: OutreachSettingsUpdate) => void;
  /** Task 139c: Slack's install URL for this Client, from the API. */
  onConnectSlack: () => Promise<string>;
  /** Re-read the Clients; `tenant` is the live copy, so an install shows here. */
  onRefresh: () => void;
  isPending: boolean;
  error: unknown;
}) {
  const current = tenant.outreach;
  const [tab, setTab] = useState<Tab>('enable');
  const [enabled, setEnabled] = useState(current.enabled);
  const [checklist, setChecklist] = useState(current.checklist);
  const [channels, setChannels] = useState<OutreachChannel[]>(current.channels);
  const [windowMin, setWindowMin] = useState(String(Math.round(current.window_s / 60)));
  const [cap, setCap] = useState(String(current.max_exchanges_per_day));
  const [contactName, setContactName] = useState(current.security_contact?.name ?? '');
  const [contactEmail, setContactEmail] = useState(current.security_contact?.email ?? '');
  const [contactPhone, setContactPhone] = useState(current.security_contact?.phone ?? '');
  const [slackError, setSlackError] = useState<unknown>(null);
  const [slackOpened, setSlackOpened] = useState(false);
  /** The install the modal opened with, to notice a new one (or a reconnect). */
  const [slackSeen] = useState(current.slack?.installed_at ?? null);
  const slackArrived = slackOpened && (current.slack?.installed_at ?? null) !== slackSeen;
  const unavailable = current.channels_unavailable;

  // After Connect Slack, re-read every 3 seconds (for up to 10 minutes) until
  // the install lands, then tick Slack: the admin connected it to use it.
  useEffect(() => {
    if (!slackOpened || slackArrived) return;
    const started = Date.now();
    const timer = window.setInterval(() => {
      if (Date.now() - started > 10 * 60_000) window.clearInterval(timer);
      else onRefresh();
    }, 3_000);
    return () => window.clearInterval(timer);
  }, [slackOpened, slackArrived, onRefresh]);
  useEffect(() => {
    if (slackArrived) setChannels((cs) => (cs.includes('slack') ? cs : [...cs, 'slack']));
  }, [slackArrived]);

  const windowValue = Number(windowMin);
  const capValue = Number(cap);
  const allConfirmed = CHECKLIST.every((c) => checklist[c.key]);
  const numbersOk =
    Number.isInteger(windowValue) && windowValue >= 5 && windowValue <= 60 &&
    Number.isInteger(capValue) && capValue >= 1 && capValue <= 500;
  const contact = contactOf(contactName, contactEmail, contactPhone);
  const contactError = contactProblem(contact);
  const canSubmit =
    numbersOk && channels.length > 0 && contactError === null && (!enabled || allConfirmed) && !isPending;
  /** Which tab holds something that stops the save, so its label can say so. */
  const tabProblem: Record<Tab, boolean> = {
    enable: enabled && !allConfirmed,
    channels: channels.length === 0,
    contact: !numbersOk || contactError !== null,
  };

  // The window opens on the click, before the API answers, or a popup
  // blocker would stop it; it is pointed at Slack once the URL arrives.
  async function connectSlack() {
    setSlackError(null);
    const opened = window.open('', '_blank');
    try {
      const url = await onConnectSlack();
      if (opened) {
        opened.opener = null;
        opened.location.href = url;
      } else {
        window.location.assign(url);
      }
      setSlackOpened(true);
    } catch (e) {
      opened?.close();
      setSlackError(e);
    }
  }

  return (
    <ModalShell onClose={onCancel} width="max-w-lg">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) {
            onSubmit({
              enabled,
              checklist,
              channels,
              window_s: windowValue * 60,
              max_exchanges_per_day: capValue,
              security_contact: contact,
            });
          }
        }}
        className="flex max-h-[calc(100dvh-2rem)] flex-col"
      >
        <div className="shrink-0 border-b border-bluesphere-border px-6 pt-5">
          <h2 className="text-lg font-semibold text-bluesphere-textOnLight">
            Outreach for {tenant.display_name}
          </h2>
          <p className="mt-1 text-sm text-bluesphere-textSecondary">
            BlueSphere asks the affected user and their manager: was this you? A <strong>no</strong>{' '}
            makes the alert actionable; a <strong>yes</strong> resolves it only once confirmed from
            the phone in their directory record.
          </p>
          <div role="tablist" className="mt-4 flex gap-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => setTab(t.key)}
                className={`-mb-px border-b-2 px-3 pb-2 text-sm font-medium ${
                  tab === t.key
                    ? 'border-bluesphere-secondary text-bluesphere-textOnLight'
                    : 'border-transparent text-bluesphere-textSecondary hover:text-bluesphere-textOnLight'
                }`}
              >
                {t.label}
                {tabProblem[t.key] && (
                  <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-red-600 align-middle" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {tab === 'enable' && (
            <>
              <fieldset className="space-y-3">
                <legend className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
                  Before enabling
                </legend>
                {CHECKLIST.map((item) => (
                  <label key={item.key} className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={checklist[item.key]}
                      onChange={(e) => setChecklist({ ...checklist, [item.key]: e.target.checked })}
                      className="mt-1 h-4 w-4 rounded-sm border-bluesphere-borderMuted text-bluesphere-secondary"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-bluesphere-textOnLight">
                        {item.label}
                      </span>
                      <span className="block text-xs text-bluesphere-muted">{item.hint}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
              <Switch
                checked={enabled}
                onChange={setEnabled}
                disabled={!enabled && !allConfirmed}
                label="Outreach enabled"
                hint={
                  allConfirmed || enabled
                    ? `Sent as "${current.sender}". Nothing is sent while this is off.`
                    : 'Confirm every item above to enable it.'
                }
              />
              {current.enabled_by && current.enabled_at && (
                <p className="text-xs text-bluesphere-muted">
                  Last enabled {new Date(current.enabled_at).toLocaleString()}.
                </p>
              )}
            </>
          )}

          {tab === 'channels' && (
            <fieldset className="space-y-2">
              <legend className="sr-only">Channels</legend>
              {CHANNELS.map((c) => {
                const why = channels.includes(c.key) ? undefined : unavailable[c.key];
                const slackRow = c.key === 'slack';
                return (
                  <div key={c.key} className="flex items-start justify-between gap-3">
                    <label
                      className={`flex min-w-0 items-start gap-3 text-sm ${
                        why ? 'cursor-not-allowed text-bluesphere-muted' : 'cursor-pointer text-bluesphere-textOnLight'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={channels.includes(c.key)}
                        disabled={why !== undefined}
                        onChange={(e) =>
                          setChannels(
                            e.target.checked
                              ? [...channels, c.key]
                              : channels.filter((x) => x !== c.key),
                          )
                        }
                        className="mt-0.5 h-4 w-4 rounded-sm border-bluesphere-borderMuted text-bluesphere-secondary"
                      />
                      <span className="min-w-0">
                        <span className="block">
                          {c.label}
                          {slackRow && current.slack && (
                            <span className="text-bluesphere-muted">
                              {' '}
                              · {current.slack.team_name ?? current.slack.team_id}
                            </span>
                          )}
                        </span>
                        {slackRow && slackOpened && !slackArrived ? (
                          <span className="block text-xs">
                            Waiting for the install to be approved in Slack…
                          </span>
                        ) : slackRow && slackArrived ? (
                          <span className="block text-xs text-bluesphere-textSecondary">
                            Connected. Save to use it.
                          </span>
                        ) : (
                          why && !slackRow && <span className="block text-xs">{why}</span>
                        )}
                      </span>
                    </label>
                    {slackRow && (
                      <PillButton
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={() => void connectSlack()}
                      >
                        {current.slack ? 'Reconnect' : 'Connect Slack'}
                      </PillButton>
                    )}
                  </div>
                );
              })}
              <ErrorNote error={slackError} />
              <p className="pt-2 text-xs text-bluesphere-muted">
                A yes is confirmed from the phone in the directory, by a text, or by a call when texts
                are off. A user with no mobile number there can say no or not sure, but not yes.
              </p>
            </fieldset>
          )}

          {tab === 'contact' && (
            <>
              <fieldset className="space-y-2">
                <legend className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
                  Security contact
                </legend>
                <p className="text-xs text-bluesphere-muted">
                  Asked at half the wait when neither the user nor their manager has answered, and
                  told how it ended. Their yes counts only when confirmed from this phone.
                </p>
                <input
                  aria-label="Security contact name"
                  placeholder="Name"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className={INPUT}
                />
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <input
                    aria-label="Security contact email"
                    type="email"
                    placeholder="Email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className={INPUT}
                  />
                  <input
                    aria-label="Security contact phone"
                    type="tel"
                    placeholder="+15555550100"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    className={INPUT}
                  />
                </div>
                {contactError && contact !== null && (
                  <p className="text-xs text-red-700">{contactError}</p>
                )}
              </fieldset>

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
                    Wait for an answer
                  </span>
                  <input
                    type="number"
                    min={5}
                    max={60}
                    value={windowMin}
                    onChange={(e) => setWindowMin(e.target.value)}
                    className={INPUT}
                  />
                  <span className="mt-1 block text-xs text-bluesphere-muted">
                    Minutes. Then the rule's timeout outcome applies.
                  </span>
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
                    Daily cap
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={cap}
                    onChange={(e) => setCap(e.target.value)}
                    className={INPUT}
                  />
                  <span className="mt-1 block text-xs text-bluesphere-muted">
                    Questions per day. Past it, alerts are escalated without asking.
                  </span>
                </label>
              </div>
            </>
          )}
        </div>

        <div className="shrink-0 space-y-2 border-t border-bluesphere-border px-6 py-4">
          <ErrorNote error={error} />
          <div className="flex items-center justify-end gap-2">
            <PillButton variant="outline" size="sm" type="button" onClick={onCancel}>
              Cancel
            </PillButton>
            <PillButton size="sm" type="submit" disabled={!canSubmit}>
              {isPending ? 'Saving…' : 'Save settings'}
            </PillButton>
          </div>
        </div>
      </form>
    </ModalShell>
  );
}
